import { useState } from 'react';

import {
  Button,
  ButtonGroup,
  FormField,
  FormHelp,
  Heading,
  Label,
  Paragraph,
  Textarea,
  TextButton,
} from '@zillow/constellation-10';
import { LeaseStatus } from '@zg-rentals/ts-rental-manager-api-client';
import { FULLSTORY_EXCLUDE } from '../../../constants/analytics';
import LeaseModal from '../../Containers/LeaseModal';
import { useCancelSigningMutation } from '../../../utils/queries';
import { LeaseArtifactType } from '@zg-rentals/ts-leasing-client';
import WhoHasSigned from '../../Widgets/WhoHasSigned';
import type { LeaseActionProps } from '../../../models/Types';
import { useLeasingUrlParams } from '../../../hooks';

export default function OpenModalCancelSigningLeaseExtension({
  terminateActiveAction,
  lease,
}: LeaseActionProps): JSX.Element {
  const [cancellationReason, setCancellationReason] = useState('');
  const { renewalId } = useLeasingUrlParams();
  const latestExtension = lease?.latestExtension ?? null;
  const extensionId = renewalId || latestExtension?.id || '';

  const cancelSigningMutation = useCancelSigningMutation();

  const isOfflineSigning = latestExtension?.status === LeaseStatus.SENT_FOR_OFFLINE_SIGNING;

  function onCancellationReasonChange(event: React.FormEvent<HTMLTextAreaElement>): void {
    setCancellationReason(event.currentTarget.value);
  }

  async function onYesCancelClick() {
    await cancelSigningMutation.mutateAsync({
      postData: {
        leaseArtifactId: extensionId as unknown as number,
        leaseArtifactType: LeaseArtifactType.LEASE_EXTENSION_RENEWAL,
        message: cancellationReason || '',
      },
    });
    terminateActiveAction();
  }

  return (
    <LeaseModal
      header={<Heading level={1}>Cancel lease renewal signing?</Heading>}
      isOpen
      onClose={terminateActiveAction}
      body={
        <>
          {!isOfflineSigning && (
            <Paragraph css={{ marginBottom: 'tight' }}>
              When you cancel signing, all signatures will be removed. After you update the lease renewal agreement you
              can request signatures again.
            </Paragraph>
          )}
          <WhoHasSigned leaseExtensionId={extensionId} isInitiallyExpanded />
          <FormField
            required
            disabled={cancelSigningMutation.isPending}
            css={{ marginTop: 'tight' }}
            className={FULLSTORY_EXCLUDE}
            label={<Label aria-label="cancellation reason">Cancellation reason</Label>}
            control={
              <Textarea
                aria-labelledby="cancellation reason"
                css={{ resize: 'vertical' }}
                value={cancellationReason}
                onChange={onCancellationReasonChange}
              />
            }
            description={<FormHelp>This will be sent to all your tenants</FormHelp>}
          />
        </>
      }
      footer={
        <ButtonGroup aria-label="cancel signing options">
          <TextButton tone="brand" onClick={terminateActiveAction} disabled={cancelSigningMutation.isPending}>
            No
          </TextButton>
          <Button
            tone="brand"
            emphasis="filled"
            disabled={!cancellationReason}
            loading={cancelSigningMutation.isPending}
            onClick={onYesCancelClick}
          >
            Yes
          </Button>
        </ButtonGroup>
      }
    />
  );
}
